import { useId } from 'react';

import { css } from '@compiled/react';

import {
	ConfluenceIcon,
	JiraIcon,
	JiraServiceManagementIcon,
	JiraProductDiscoveryIcon,
	JiraAlignIcon,
	LoomIcon,
	TrelloIcon,
	BitbucketIcon,
	CompassIcon,
	GuardIcon,
	FocusIcon,
	TalentIcon,
	AssetsIcon,
	CustomerServiceManagementIcon,
	BambooIcon,
	StatuspageIcon,
	CrowdIcon,
} from '@atlaskit/logo-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
} from '@/libraries/design/foundations/grid/src/base';
import { RichTextComponent } from '@/libraries/design/utils';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

import { Button, ButtonProps, ButtonSize, ButtonVariant } from '../../button/src/button';
import { Image, ImageProps } from '../../image/src/image';
import { Link, LinkProps, BgColors } from '../../link/src/link';

export interface ButtonAndLinkProps {
	button: ButtonProps;
	buttonText: string;
	buttonVariant?: ButtonVariant;
	link?: LinkProps;
	dataEventProps?: DataEventProps;
}

export interface OneLinkProps {
	link: LinkProps;
}

export type ProductCardBlockCtaProps = ButtonAndLinkProps | OneLinkProps;

export type ProductCardBlockTitleAs = 'h2' | 'h3' | 'h4';
export type ProductCardBlockAlignment = 'start' | 'center';

export type ResponsiveProductCardBlockAlignment = {
	mobile: ProductCardBlockAlignment;
	tablet: ProductCardBlockAlignment;
	desktop: ProductCardBlockAlignment;
};

export type IconProps = {
	linkedIcon?: {
		url: string;
		size?: keyof typeof iconSizes;
		alt?: string;
	};
	iconComponent?: JSX.Element;
};

export type ProductCardBlockColumns = 10 | 12; // Currently only 10 or 12 columns are supported

export type ProductCardBlockLogoLockupsProps = {
	[key: string]: {
		src: string | JSX.Element;
		alt: string;
		text?: string;
		height?: number;
		image?: boolean;
	};
};

export const ProductCardBlockLogoLockups: ProductCardBlockLogoLockupsProps = {
	jira: {
		src: <JiraIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Jira logo',
		text: 'Jira',
	},
	jiraProductDiscovery: {
		src: <JiraProductDiscoveryIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Jira Product Discovery logo',
		text: 'Jira Product Discovery',
	},
	jiraServiceManagement: {
		src: <JiraServiceManagementIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Jira Service Management logo',
		text: 'Jira Service Management',
	},
	statuspage: {
		src: <StatuspageIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Statuspage logo',
		text: 'Statuspage',
	},
	confluence: {
		src: <ConfluenceIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Confluence logo',
		text: 'Confluence',
	},
	trello: {
		src: <TrelloIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Trello logo',
		text: 'Trello',
	},
	loom: {
		src: <LoomIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Loom logo',
		text: 'Loom',
	},
	bitbucket: {
		src: <BitbucketIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Bitbucket logo',
		text: 'Bitbucket',
	},
	rovoDev: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/m075131gef201aeok8g484w063t363hc.svg',
		alt: 'Rovo Dev logo',
		text: 'Rovo Dev',
		image: true,
	},
	compass: {
		src: <CompassIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Compass logo',
		text: 'Compass',
	},
	guard: {
		src: <GuardIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Guard logo',
		text: 'Guard',
	},
	focus: {
		src: <FocusIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Focus logo',
		text: 'Focus',
	},
	talent: {
		src: <TalentIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Talent logo',
		text: 'Talent',
	},
	rovo: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/qe401oio4a2o006h83v3861f5xg6gc76.svg',
		alt: 'Rovo logo',
		text: 'Rovo',
		image: true,
	},
	jiraAlign: {
		src: <JiraAlignIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Jira Align logo',
		text: 'Jira Align',
	},
	assets: {
		src: <AssetsIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Assets logo',
		text: 'Assets',
	},
	customerServiceManagement: {
		src: <CustomerServiceManagementIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Customer Service Management logo',
		text: 'Customer Service Management',
	},
	bamboo: {
		src: <BambooIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Bamboo logo',
		text: 'Bamboo',
	},
	pipelines: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/w5y63772u3hv13stbj352ut7x2h001jf.svg',
		alt: 'Pipelines logo',
		text: 'Pipelines',
		image: true,
	},
	dx: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1mx06b178gpdjg8u7kjg38ci80p80eln.svg',
		alt: 'DX logo',
		text: 'DX',
		image: true,
	},
	sourcetree: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1o5et30ftpu1nt3m25bbcmg44k5apcr6.svg',
		alt: 'SourceTree logo',
		text: 'SourceTree',
		image: true,
	},
	crowd: {
		src: <CrowdIcon appearance="brand" size="medium" shouldUseNewLogoDesign />,
		alt: 'Crowd logo',
		text: 'Crowd',
	},
	teamworkCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/78ylq28783ef6w7c21vu5y84ngku10fx.svg',
		alt: 'Teamwork Collection logo',
		height: 20,
	},
	strategyCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/h5mj8675hs2i21g7687jagjoax1uy14n.svg',
		alt: 'Strategy Collection logo',
		height: 20,
	},
	serviceCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/f481qls2e2g883b2us2ek546yg4c44g1.svg',
		alt: 'Service Collection logo',
		height: 20,
	},
	softwareCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1j5jdr1kj625ayo48v421j3o05hg8nkg.svg',
		alt: 'Software Collection logo',
		height: 20,
	},
	productCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12NA4X.svg',
		alt: 'Product Collection',
		height: 20,
	},
};

export interface ProductCardBlockProps {
	logo?: keyof typeof ProductCardBlockLogoLockups;
	eyebrow?: string;
	title?: string;
	text?: string | React.ReactElement<typeof RichTextComponent>;
	titleAs?: ProductCardBlockTitleAs;
	imageProps?: ImageProps;
	spotIllustrationUrl?: string;
	icon?: IconProps;
	callToActionProps?: ProductCardBlockCtaProps;
	alignment?: ProductCardBlockAlignment | ResponsiveProductCardBlockAlignment;
	orientation?: 'horizontal' | 'vertical' | 'horizontal-reverse';
	columns?: ProductCardBlockColumns;
	backgroundColor?: string;
}

interface RenderCtaProps {
	ctaProps: ProductCardBlockCtaProps;
	buttonSize: ButtonSize;
	alignment: ProductCardBlockAlignment | ResponsiveProductCardBlockAlignment;
	backgroundColor?: string;
	titleId?: string;
	buttonId?: string;
	linkId?: string;
}

const imageStyle = css({
	// Issue in Picture component applies styles to picture instead of img
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		aspectRatio: '16 / 9',
		width: '100%',
		height: 'auto',
		objectFit: 'contain',
	},
});

const zeroMarginCss = css({ margin: 0 });

const iconSizes = {
	'x-small': 24,
	small: 32,
	medium: 48,
	large: 64,
};

type ProductCardBlockColumnsInfo = 'assetSpan' | 'contentStart';

const contentColumnsLayout: Record<
	ProductCardBlockColumns,
	Record<ProductCardBlockColumnsInfo, number>
> = {
	10: { assetSpan: 3, contentStart: 2 }, // 10 columns content block means 3 columns for asset, 7 for text
	12: { assetSpan: 4, contentStart: 1 },
	// Add more mappings as needed in the future, ex:
	// 8: 2 // 8 columns content block means 2 columns for asset, 6 for text
};

const renderCta = ({
	ctaProps,
	buttonSize,
	alignment,
	backgroundColor,
	titleId,
	buttonId,
	linkId,
}: // eslint-disable-next-line complexity
RenderCtaProps) => {
	if ('button' in ctaProps && ctaProps.button !== undefined) {
		return (
			<div
				css={css({
					margin: 0,
					display: 'flex',
					flexWrap: 'wrap',
					gap: token('space.250'),
					alignItems: 'center',
					justifyContent: typeof alignment === 'string' ? alignment : alignment.mobile,
					[media.above.sm]: {
						justifyContent: typeof alignment === 'string' ? alignment : alignment.tablet,
					},
					[media.above.md]: {
						justifyContent: typeof alignment === 'string' ? alignment : alignment.desktop,
					},
				})}
			>
				<Button
					{...ctaProps.button}
					id={buttonId}
					size={buttonSize}
					variant={
						backgroundColor &&
						backgroundColor === '#1868DB' &&
						(ctaProps.buttonVariant === 'primary' || !ctaProps.buttonVariant)
							? 'secondary'
							: (ctaProps.buttonVariant ?? 'primary')
					}
					colorAgainst={backgroundColor === '#1868DB' ? 'other' : 'white'}
					css={css({ flexBasis: 'max-content' })}
					ariaLabelledby={`${buttonId} ${titleId}`}
					dataEventProps={ctaProps.dataEventProps}
				>
					{ctaProps.buttonText}
				</Button>
				{ctaProps.link && (
					// eslint-disable-next-line @atlassian/a11y/anchor-has-content -- Link component renders text content via the text prop from ctaProps.link
					<Link
						id={linkId}
						ariaLabelledby={`${linkId} ${titleId}`}
						type="primary"
						bgColor={
							(isDarkBackgroundColor(backgroundColor) ? 'bgBlackBlue' : 'bgWhite') as BgColors
						}
						{...ctaProps.link}
					/>
				)}
			</div>
		);
	}

	return (
		<div css={zeroMarginCss}>
			{/* @ts-expect-error TS 5.9 */}
			<Link
				id={linkId}
				ariaLabelledby={`${linkId} ${titleId}`}
				type="primary"
				bgColor={(isDarkBackgroundColor(backgroundColor) ? 'bgBlackBlue' : 'bgWhite') as BgColors}
				{...ctaProps.link}
			>
				{ctaProps.link?.text}
			</Link>
		</div>
	);
};

const renderIcon = (icon: IconProps) => {
	if (icon?.linkedIcon) {
		const linkedIconSize = icon?.linkedIcon?.size
			? iconSizes[icon.linkedIcon.size]
			: iconSizes['medium'];

		return (
			<div>
				<Image
					src={icon.linkedIcon.url}
					alt={icon.linkedIcon.alt ?? ''}
					width={linkedIconSize}
					height={linkedIconSize}
				/>
			</div>
		);
	}
	if (icon?.iconComponent) {
		return <div>{icon.iconComponent}</div>;
	}
	return null;
};

const isDarkBackgroundColor = (color?: string): boolean => {
	return color ? ['#1868DB', '#101214'].includes(color) : false;
};

export const ProductCardBlock = ({
	logo,
	eyebrow,
	title,
	text,
	imageProps,
	spotIllustrationUrl,
	icon,
	callToActionProps,
	alignment = 'start',
	orientation = 'vertical',
	columns = 10,
	backgroundColor,
	...props
}: // eslint-disable-next-line complexity -- ProductCardBlock component has high complexity due to multiple layout variations and conditional rendering
ProductCardBlockProps) => {
	// hedge against the value being passed in as a string (the options in uniform can only be string values)
	// which would subsequently fail a string vs number check ie 10 === "10" is false
	const numColumns = Number(columns) as ProductCardBlockColumns;

	const { titleAs: TitleComponent = 'h3' } = props;

	const isSmallDetailStyling = ['h4'].includes(TitleComponent as string);
	const isLargeTextStyling = (TitleComponent as string) === 'h2';
	const buttonSize: ButtonSize = isSmallDetailStyling ? 'small' : 'large';

	const uniqueId = useId();
	const titleId = `product-card-block-title-${uniqueId}`;
	const buttonId = `product-card-block-button-${uniqueId}`;
	const linkId = `product-card-block-link-${uniqueId}`;

	const lightTextStyles = css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		p: {
			color: `${wacToken('color.neutral.light0')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.body-lg, .body-md': {
			color: `${wacToken('color.neutral.light0')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'h2, h3, h4': {
			color: `${wacToken('color.neutral.light0')}`,
		},
	});

	const textElement: React.ReactNode =
		typeof text === 'string' ? (
			<p
				className={isLargeTextStyling ? 'body-lg' : 'body-md'}
				css={[zeroMarginCss, isDarkBackgroundColor(backgroundColor) && lightTextStyles]}
			>
				{text}
			</p>
		) : (
			text
		);

	const bgColorStyle = css({
		'--dxp-block-background-color': backgroundColor,
	});

	const textContent = (
		<div
			css={[
				css({
					display: 'flex',
					flexDirection: 'column',
					gap: token('space.300'),
					paddingBlockStart: orientation === 'horizontal' ? token('space.100') : undefined,
					justifyContent: 'space-between',
					[media.above.sm]: {
						gridColumn: 'var(--text-grid-column)',
					},
					height: '100%',
				}),
				isDarkBackgroundColor(backgroundColor) && lightTextStyles,
			]}
		>
			<div
				css={css({
					display: 'flex',
					flexDirection: 'column',
					gap: token('space.300'),
				})}
			>
				{logo && (
					<div
						css={css({
							display: 'flex',
							alignItems: 'flex-start',
							gap: token('space.100'),
						})}
					>
						{ProductCardBlockLogoLockups[logo] && 'text' in ProductCardBlockLogoLockups[logo] ? (
							<>
								{ProductCardBlockLogoLockups[logo].image ? (
									<Image
										src={ProductCardBlockLogoLockups[logo]?.src as string}
										alt={ProductCardBlockLogoLockups[logo]?.alt}
										css={css({
											width: 32,
										})}
									/>
								) : (
									ProductCardBlockLogoLockups[logo].src
								)}
								<span
									css={css({
										fontFamily: wacToken('font.charlie.display'),
										fontSize: '22px',
										fontStyle: 'normal',
										fontWeight: '400',
										lineHeight: '30px',
										margin: 0,
										display: 'block',
									})}
								>
									{
										(
											ProductCardBlockLogoLockups[logo] as {
												text: string;
											}
										).text
									}
								</span>
							</>
						) : (
							<Image
								src={ProductCardBlockLogoLockups[logo]?.src as string}
								alt={ProductCardBlockLogoLockups[logo]?.alt as string}
								height={ProductCardBlockLogoLockups[logo]?.height ?? 32}
								css={css({
									width: '100%',
									objectFit: 'contain',
									objectPosition: 'left center',
								})}
							/>
						)}
					</div>
				)}
				<div
					className="product-card-block-text-content"
					css={css({
						display: 'flex',
						flexDirection: 'column',
						gap: token('space.100'),
					})}
				>
					{eyebrow && (
						<p
							className={isSmallDetailStyling ? 'eyebrow-sm' : 'eyebrow-md'}
							css={[
								zeroMarginCss,
								css({
									color: `${wacToken('color.neutral.light800')} !important`,
								}),
							]}
						>
							{eyebrow}
						</p>
					)}
					{title && (
						<TitleComponent className="card-title" id={titleId} css={zeroMarginCss}>
							{title}
						</TitleComponent>
					)}
					{text && textElement}
				</div>
			</div>
			{callToActionProps &&
				renderCta({
					ctaProps: callToActionProps,
					buttonSize,
					alignment,
					backgroundColor,
					titleId,
					buttonId,
					linkId,
				})}
		</div>
	);

	const assetStyles = css({
		[media.above.sm]: {
			gridColumn: 'var(--asset-grid-column)',
		},
	});

	const horizontalSpotIllustrationStyles = css({
		[media.above.sm]: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
	});

	const assetContent = imageProps ? (
		<div css={[assetStyles]}>
			<Image {...imageProps} css={imageStyle} alt={imageProps.alt ?? ''} />
		</div>
	) : spotIllustrationUrl ? (
		<div css={[assetStyles, horizontalSpotIllustrationStyles]}>
			<Image src={spotIllustrationUrl} alt="" width={236} height={236} />
		</div>
	) : icon ? (
		renderIcon(icon)
	) : null;

	const gapBetweenIconAndText = css({
		gap: token('space.200'),
	});

	const productCardPadding = css({
		padding: token('space.300'),
	});

	if (orientation === 'vertical') {
		return (
			<div
				css={[
					bgColorStyle,
					productCardPadding,
					css({
						textAlign: typeof alignment === 'string' ? alignment : alignment.mobile,
						[media.above.sm]: {
							textAlign: typeof alignment === 'string' ? alignment : alignment.tablet,
						},
						[media.above.md]: {
							textAlign: typeof alignment === 'string' ? alignment : alignment.desktop,
						},
						display: 'flex',
						flexDirection: 'column',
						gap: token('space.300'), // for image or spot illustration
						background: 'var(--dxp-block-background-color, transparent)',
						alignItems: 'stretch',
						justifyContent: 'stretch',
						height: '100%',
					}),
					icon && gapBetweenIconAndText,
					borderRadiusStyle,
				]}
			>
				{assetContent}
				{textContent}
			</div>
		);
	}

	const fullWidthGridStyles = css({
		marginInline: 'auto',
		maxWidth: '100%',
		width: '100%',
		'--width-without-gaps': 'calc(100% - var(--cl-gap) * 11)', // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
		[media.above.sm]: {
			gridTemplateColumns: 'repeat(12, 1fr)',
		},
		[media.above.md]: {
			'--cl-gap': '32px',
			maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
		},
		display: 'grid',
		boxSizing: 'border-box',
		gap: 'var(--cl-gap)',
		'--cl-gap': '24px',
	});

	const horizontalProductCard = (
		<div
			css={[
				productCardPadding,
				fullWidthGridStyles,
				bgColorStyle,
				css({
					position: 'relative',
					zIndex: 1,
					'--text-grid-column': `${
						orientation === 'horizontal-reverse'
							? contentColumnsLayout[numColumns]['contentStart']
							: contentColumnsLayout[numColumns]['contentStart'] +
								contentColumnsLayout[numColumns]['assetSpan']
					} / span ${numColumns - contentColumnsLayout[numColumns]['assetSpan']}`,
					'--asset-grid-column': `${
						orientation === 'horizontal'
							? contentColumnsLayout[numColumns]['contentStart']
							: contentColumnsLayout[numColumns]['contentStart'] +
								numColumns -
								contentColumnsLayout[numColumns]['assetSpan']
					} / span ${contentColumnsLayout[numColumns]['assetSpan']}`,
				}),
				!backgroundColor && borderRadiusStyle,
			]}
		>
			{orientation === 'horizontal' ? assetContent : null}
			{textContent}
			{orientation === 'horizontal-reverse' ? assetContent : null}
		</div>
	);

	switch (numColumns) {
		case 10:
		case 12:
			if (backgroundColor) {
				return (
					<div
						css={[
							bgColorStyle,
							borderRadiusStyle,
							css({
								width: '100%',
								position: 'relative',
							}),
						]}
					>
						<div
							css={[
								css({
									position: 'absolute',
									width: '100vw',
									[media.above.sm]: {
										width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
										maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))`, // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
									},
									height: '100%',
									insetInlineStart: '50%',
									transform: 'translateX(-50%)',
									background: 'var(--dxp-block-background-color, transparent)',
									borderRadius: wacToken('corner.radius.card'),
								}),
							]}
						/>
						{horizontalProductCard}
					</div>
				);
			}
			return horizontalProductCard;
	}
};

const borderRadiusStyle = css({
	borderRadius: wacToken('corner.radius.card'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
});
